/**
 * SearchBar — the client-name filter imprinted from the Case Portal's `.search`
 * (portal.html): a rounded input with a leading magnifier and the portal's soft
 * orange focus halo (ADR-0138, var-driven so ground candidates re-pitch it).
 * Controlled; the book owns the query state and does the filtering, so
 * each book filters its own name field. Renders a clearable affordance when
 * non-empty.
 */

import { Search, X } from "lucide-react";
import { foldName } from "@command-center/shared";

export function SearchBar({
  value,
  onChange,
  placeholder = "Search by client name…",
  testId = "search-bar",
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  testId?: string;
}) {
  return (
    <div className="relative mb-3">
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        data-testid={testId}
        className="w-full rounded-lg border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-active-edge focus:shadow-halo focus:outline-none"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          data-testid={`${testId}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

/** Substring match ignoring case and accents (foldName, #879), null-safe — the shared
 *  predicate books use to filter by client name. An empty query matches everything. */
export function matchesQuery(name: string | null | undefined, query: string): boolean {
  const q = foldName(query.trim());
  if (q === "") return true;
  return foldName(name ?? "").includes(q);
}
